import { Mapping } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { theme } from '@frontend/theme';
import { Avatar, Truncated } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { SETTINGS_TRACKING_IDS } from '../../trackingIds/settings-tracking-ids';
import { AppointmentTypeChip } from '../appointment-type-chip';
import { GenericChipsList } from '../generic-chip-list';
import { VisibilityToggle } from './VisibilityToggle';

export const ProviderNameCell = ({ provider }: { provider: Provider }) => {
  const { t } = useTranslation('scheduleAppointmentTypes');

  const name = getFullName(provider);
  return (
    <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), width: '100%' }}>
      {provider.isArchived ? (
        <Avatar
          size='small'
          iconName='archive-small'
          title={t('Archived')}
          css={{ svg: { color: theme.colors.icon.subdued } }}
          disableClick
        />
      ) : (
        <Avatar size='small' src={provider.profile?.picUrl} name={name} />
      )}
      <Truncated>{provider.displayName}</Truncated>
    </div>
  );
};

export const AppointmentTypesCell = ({
  provider,
  mappingList,
  canUseAppointmentColors,
}: {
  provider: Provider;
  mappingList: Mapping[];
  canUseAppointmentColors: boolean;
}) => {
  const practitionerId = provider.id;
  const practitionerAppointmentTypeList = mappingList
    .filter(
      (item) =>
        item.providerMetaData?.id === practitionerId &&
        !!item.appointmentTypeMetaData?.id &&
        item.appointmentTypeMetaData.name
    )
    .map((item) => ({
      name: item.appointmentTypeMetaData?.name ?? '',
      color: canUseAppointmentColors ? item.appointmentTypeMetaData?.appointmentTypeColor || PRIMARY_EVENT_COLOR : '',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (practitionerAppointmentTypeList.length === 0) return '-';

  return (
    <GenericChipsList
      items={practitionerAppointmentTypeList}
      renderChip={({ name, color }) => <AppointmentTypeChip key={name} name={name} color={color} />}
    />
  );
};

export const OnlineSchedulingCell = ({ provider }: { provider: Provider }) => (
  <VisibilityToggle
    providerDetails={provider}
    value={!!provider.profile?.sobs}
    disabled={provider.isArchived}
    trackingId={SETTINGS_TRACKING_IDS.manageProviders.onlineSchedulingToggleButton}
  />
);

export const ShowOnCalendarCell = ({
  provider,
  isScheduleDefinitionsEnabled,
  isOnlineSchedulingEnabled,
}: {
  provider: Provider;
  isScheduleDefinitionsEnabled: boolean;
  isOnlineSchedulingEnabled: boolean;
}) => (
  <VisibilityToggle
    showOnCalendarToggle
    providerDetails={provider}
    value={!provider.hidden}
    disabled={
      (!isScheduleDefinitionsEnabled && isOnlineSchedulingEnabled && provider.profile?.sobs) || provider.isArchived
    }
    indeterminate={provider.isArchived}
    trackingId={SETTINGS_TRACKING_IDS.manageProviders.showOnCalendarToggleButton}
  />
);
